import Async from "react-select/async";
import { api } from "../../../services/api/api";
import { setSelectList } from "../../../services/utils/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import { reactSelectTheme } from "../../option-select/reactSelectTheme";
/***/

/**
* ServiceSelect: Allow selection of a service
* @param data - previous selected data
* @param onChange - on data change event
* @param className - additional classname
* @param isMulti - allow multiple selection
* @param placeholder - placeholder text for the select input
* @param classNamePrefix - prefix for react-select classNames
* @param disabled - whether the select is disabled
*/
export default function ServiceSelect<T extends { label: string }>(
    {
        data,
        onChange,
        className = "",
        isMulti = false,
        placeholder,
        classNamePrefix = "react-select",
        disabled,
        isClearable = true,
    }: {
        data?: T | T[] | null,
        onChange: (e: T | T[] | null) => void,
        className?: string,
        isMulti?: boolean,
        placeholder?: string,
        classNamePrefix?: string,
        disabled?: boolean,
        isClearable?: boolean,
    }
) {
    const { t } = useTranslation();
    const [valueState, setValueState] = useState<any>(null);

    /**
    * useEffect: Transform value to be printed in select
    * @param data - reload on value change
    */
    useEffect(() => {
        if (data) {
            if (isMulti) {
                const tmp = data as T[];
                setValueState(tmp.map(el => ({ value: el, label: el.label })));
            } else {
                const tmp = data as T;
                setValueState({ value: tmp, label: tmp.label });
            }
        } else {
            setValueState(null);
        }
    }, [data, isMulti]);
    /***/

    /**
    * getServiceList: get services list
    * @param inputValue - search input
    * @return - printable list for select
    */
    const getServiceList = (inputValue?: string): Promise<EasyTech.IFormData<T>[]> => {
        return new Promise((resolve) => {
            let queries: { queries: Array<{ type: string; operator: string; field: string[]; value: string | undefined; }> } = {
                queries: [{
                    type: "SEARCH",
                    operator: "LIKE",
                    field: ["label", "id"],
                    value: inputValue,
                }]
            };

            if (!inputValue) {
                queries = { queries: [] };
            }

            api.post("users/services/search", queries)
                .then((ret: any) => {
                    const dataList = ret.data as T[] || ret as T[];
                    return resolve(setSelectList<T>(dataList, (el) => el.label));
                })
                .catch(() => resolve([]));
        });
    };
    /***/

    /**
    * handleChange: manage change event
    * @param e - new value
    */
    const handleChange = (e: EasyTech.IFormData<T> | EasyTech.IFormData<T>[] | null) => {
        if (e === null) {
            onChange(null);
            return;
        }
        if (isMulti) {
            const result = (e as EasyTech.IFormData<T>[]).map((el) => el.value);
            onChange(result);
        } else {
            const result = (e as EasyTech.IFormData<T>).value;
            onChange(result);
        }
    };
    /***/

    const SelectField = isMulti ? MultiCheckSelect : Async;

    return (
        <SelectField
            key={isMulti && data ? JSON.stringify((data as T[]).map(item => (item as any).id)) : "default"}
            cacheOptions
            defaultOptions
            loadOptions={(inputValue: string) => getServiceList(inputValue)}
            value={valueState}
            theme={reactSelectTheme}
            onChange={(e: EasyTech.IFormData<T> | EasyTech.IFormData<T>[] | null) => handleChange(e)}
            className={`w-full mr-2 ${className}`}
            name="service"
            placeholder={placeholder || t("common:selectService")}
            classNamePrefix={classNamePrefix}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            hideSelectedOptions={!isMulti}
            isClearable={isClearable}
            isDisabled={disabled}
        />
    );
}
/***/
